import { store } from '@/store/store'
import {
	applySlideBetCancelled,
	applySlideBetPlaced,
	applySlideRoundPlaying,
	applySlideRoundResult,
	applySlideRoundWaiting,
	selectSlideGame,
	setSlideHistory,
	setSlidePreviousResult,
	setSlideRoundPlayers,
	setSlideRoundStats,
	setSlideScheduledBet,
	setSlideSubmitting,
} from '@/store/slices/slideSlice'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { getSlideSocket, disconnectSlideSocket } from '@/store/socket/slide-socket'
import { originalApi } from '@/store/api/originalApi'
import { showApiErrorToast, showApiSuccessToast } from '@/lib/api-toast'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import { SoundManager } from '@/components/original/games/slide/core/SoundManager'
import { SLIDE_STRIP_LENGTH } from '@/config/original'
import type { SlideCancelBetRequest, SlidePlaceBetRequest, SlideRoundPlayer, SlideSocketData, SlideSocketPlayer, SlideSocketResponse } from '@/types/slide'

function getEventData(payload: unknown): SlideSocketData {
	return (payload as SlideSocketResponse).data ?? {}
}

function mapDisplayPlayers(players: SlideSocketPlayer[] | undefined): SlideRoundPlayer[] {
	if (!players?.length) {
		return []
	}

	return players.map((row) => ({
		playerId: row.player_id,
		username: row.username,
		betAmount: Number(row.amount) || 0,
		currencyCode: row.currency_code,
		avatar: row.avatar,
		targetMultiplier: Number(row.target_multiplier) || 0,
		payout: row.payout ?? null,
		isWinner: row.is_winner ?? null,
	}))
}

function coerceNumbers(values: number[] | undefined): number[] {
	if (!values?.length) {
		return []
	}

	return values.map((value) => Number(value)).filter((value) => Number.isFinite(value))
}

function applyBetUpdate(data: SlideSocketData) {
	if (data.players) {
		store.dispatch(setSlideRoundPlayers(mapDisplayPlayers(data.players)))
	}

	const slide = selectSlideGame(store.getState())
	store.dispatch(
		setSlideRoundStats({
			playerCount: data.player_count ?? slide.playerCount,
			totalBetAmount: data.total_bet_amount ?? slide.totalBetAmount,
		}),
	)
}

export class GameController {
	private disposed = false
	private lastIsAuthenticated = this.authenticated
	private unsubscribeStore: (() => void) | null = null
	private lastAnimationEnded = false

	constructor() {
		this.initSocket()
		this.subscribeToStore()
		void this.loadHistory()
	}

	dispose() {
		this.disposed = true
		this.unsubscribeStore?.()
		this.unsubscribeStore = null
		const socket = getSlideSocket()
		socket.offAny(this.handleSocketEvent)
	}

	private get slideState() {
		return selectSlideGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private requireAuth(message: string): boolean {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private async loadHistory() {
		try {
			const response = await store.dispatch(originalApi.endpoints.getSlideRoundHistory.initiate({ page: 1, per_page: 20 }, { forceRefetch: true })).unwrap()

			if (this.disposed) {
				return
			}

			const rows = response.data ?? []
			const history = rows.map((row) => Number(row.slide_point)).reverse()
			store.dispatch(setSlideHistory(history))

			const latestSlidePoint = Number(rows[0]?.slide_point)
			if (Number.isFinite(latestSlidePoint) && latestSlidePoint > 0) {
				store.dispatch(
					setSlidePreviousResult({
						slidePoint: latestSlidePoint,
						numbers: this.buildLastDisplayStrip(latestSlidePoint),
					}),
				)
			}
		} catch {
			// History is optional for first paint; gameplay still works.
		}
	}

	private buildLastDisplayStrip(slidePoint: number): number[] {
		const pointIndex = Math.floor(Math.random() * SLIDE_STRIP_LENGTH)

		return Array.from({ length: SLIDE_STRIP_LENGTH }, (_, index) => {
			if (index === pointIndex) {
				return slidePoint
			}

			return Math.floor((1 + Math.random() * 99) * 100) / 100
		})
	}

	private initSocket() {
		const socket = getSlideSocket()
		socket.offAny(this.handleSocketEvent)
		socket.onAny(this.handleSocketEvent)

		if (!socket.connected) {
			socket.connect()
		}
	}

	private subscribeToStore() {
		this.unsubscribeStore = store.subscribe(() => {
			if (this.disposed) {
				return
			}

			const isAuthenticated = this.authenticated
			if (isAuthenticated !== this.lastIsAuthenticated) {
				this.lastIsAuthenticated = isAuthenticated
				this.reconnectSocket()
			}

			const slide = this.slideState
			const animationEnded = slide.animationEnded
			if (animationEnded && !this.lastAnimationEnded && slide.phase === 'result' && slide.isWinner && slide.winAmount > 0) {
				showApiSuccessToast(`Won ${slide.winAmount.toFixed(2)} at ${Number(slide.targetMultiplier).toFixed(2)}x`)
			}
			this.lastAnimationEnded = animationEnded
		})
	}

	private reconnectSocket() {
		disconnectSlideSocket()
		this.initSocket()
	}

	private handleSocketEvent = (event: string, payload?: unknown) => {
		if (this.disposed) {
			return
		}

		const data = getEventData(payload)

		switch (event) {
			case 'roundStarted':
				this.handleRoundStarted(data)
				return
			case 'roundBettingOnHold':
				this.handleRoundBettingOnHold(data)
				return
			case 'roundStopped':
				this.handleRoundStopped(data)
				return
			case 'betUpdate':
				applyBetUpdate(data)
				return
			default:
				return
		}
	}

	private handleRoundStarted(data: SlideSocketData) {
		store.dispatch(
			applySlideRoundWaiting({
				slideGameId: data.slide_game_id ?? null,
				bettingWindowSeconds: data.betting_window_seconds ?? 5,
				publicSeed: data.public_seed,
				privateHash: data.private_hash,
			}),
		)

		applyBetUpdate(data)
		void this.maybeAutoPlaceBet()
	}

	private handleRoundBettingOnHold(data: SlideSocketData) {
		if (this.slideState.phase === 'playing') {
			return
		}

		const numbers = coerceNumbers(data.numbers)
		if (!numbers.length) {
			return
		}

		SoundManager.instance.play('slideStrip')

		store.dispatch(
			applySlideRoundPlaying({
				slideGameId: data.slide_game_id ?? null,
				numbers,
				publicSeed: data.public_seed,
				privateHash: data.private_hash,
				privateSeed: data.private_seed,
			}),
		)

		applyBetUpdate(data)
	}

	private handleRoundStopped(data: SlideSocketData) {
		const slidePoint = Number(data.slide_point)
		if (!Number.isFinite(slidePoint) || slidePoint <= 0) {
			return
		}

		if (this.slideState.phase === 'result' && this.slideState.slidePoint === slidePoint) {
			return
		}

		const slide = this.slideState
		const hadBet = slide.hasBet
		const targetMultiplier = Number(slide.targetMultiplier)
		const betAmount = Number(slide.betAmount)
		const isWinner = hadBet && Number.isFinite(targetMultiplier) && targetMultiplier <= slidePoint
		const winAmount = isWinner && Number.isFinite(betAmount) ? Number((betAmount * targetMultiplier).toFixed(6)) : 0

		store.dispatch(
			applySlideRoundResult({
				slidePoint,
				winAmount,
				isWinner,
			}),
		)

		applyBetUpdate(data)
	}

	private async maybeAutoPlaceBet() {
		if (!this.authenticated) {
			return
		}

		const slide = this.slideState

		if ((slide.autoBetEnabled || slide.scheduledBet) && !slide.hasBet) {
			await this.placeBet()
		}
	}

	async placeBet() {
		if (!this.requireAuth('Please login to place a bet')) {
			return
		}

		const slide = this.slideState

		if (slide.phase !== 'waiting') {
			if (!slide.hasBet && !slide.scheduledBet) {
				store.dispatch(setSlideScheduledBet(true))
			}
			return
		}

		if (slide.hasBet || slide.isSubmitting || !slide.slideGameId) {
			return
		}

		const betAmount = Number(slide.betAmount)
		if (!Number.isFinite(betAmount) || betAmount <= 0) {
			showApiErrorToast('Enter a valid bet amount.')
			return
		}

		const targetMultiplier = Number(slide.targetMultiplier)
		if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
			showApiErrorToast('Enter a valid target multiplier.')
			return
		}

		store.dispatch(setSlideSubmitting(true))

		const payload: SlidePlaceBetRequest = {
			slide_game_id: slide.slideGameId,
			currency_code: slide.currencyCode,
			amount: betAmount,
			target_multiplier: Number(targetMultiplier.toFixed(2)),
			idempotency_key: createIdempotencyKey(),
		}

		try {
			const response = await this.emitAck('placeBet', payload)
			const data = getEventData(response)
			store.dispatch(applySlideBetPlaced())
			applyBetUpdate(data)
			SoundManager.instance.play('chipPlace')
		} catch (error) {
			store.dispatch(setSlideSubmitting(false))
			store.dispatch(setSlideScheduledBet(false))
			const message = error instanceof Error ? error.message : 'Unable to place bet.'
			showApiErrorToast(message)
		}
	}

	async cancelBet() {
		if (!this.requireAuth('Please login to cancel a bet')) {
			return
		}

		const slide = this.slideState

		if (slide.scheduledBet && slide.phase !== 'waiting') {
			store.dispatch(setSlideScheduledBet(false))
			return
		}

		if (slide.phase !== 'waiting' || !slide.hasBet || slide.isSubmitting || !slide.slideGameId) {
			return
		}

		store.dispatch(setSlideSubmitting(true))

		const payload: SlideCancelBetRequest = {
			slide_game_id: slide.slideGameId,
		}

		try {
			const response = await this.emitAck('cancelBet', payload)
			store.dispatch(applySlideBetCancelled())
			applyBetUpdate(getEventData(response))
		} catch (error) {
			store.dispatch(setSlideSubmitting(false))
			const message = error instanceof Error ? error.message : 'Unable to cancel bet.'
			showApiErrorToast(message)
		}
	}

	private emitAck<T extends SlidePlaceBetRequest | SlideCancelBetRequest>(event: string, payload: T): Promise<SlideSocketResponse> {
		const socket = getSlideSocket()

		return new Promise((resolve, reject) => {
			socket.timeout(10000).emit(event, payload, (err: Error | null, response?: SlideSocketResponse) => {
				if (err) {
					reject(err)
					return
				}

				if (!response?.success) {
					reject(new Error(response?.error ?? response?.message ?? `${event} failed`))
					return
				}

				resolve(response)
			})
		})
	}
}
